import { useEffect, useCallback, useRef, RefObject } from 'react'
import * as echarts from 'echarts'

export const useECharts = (content: string, isDark: boolean, containerRef?: RefObject<HTMLElement | null>) => {
  const lastThemeRef = useRef<boolean>(isDark)
  // 使用唯一ID标识当前组件实例，避免多实例冲突
  const instanceIdRef = useRef<string>(`echarts-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  // 存储 ResizeObserver 实例
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  // resize 所有图表的函数
  const resizeAllCharts = useCallback(() => {
    if (!containerRef?.current) return
    const containers = containerRef.current.querySelectorAll('.echarts-container')
    containers.forEach((container) => {
      const chart = echarts.getInstanceByDom(container as HTMLElement)
      chart?.resize()
    })
  }, [containerRef])

  const initCharts = useCallback(() => {
    // 使用 setTimeout(0) 确保在 React 渲染完成后的下一个事件循环执行
    // 这比 requestAnimationFrame 在某些情况下更可靠，特别是在处理 DOM 更新时
    setTimeout(() => {
      // 必须使用传入的容器引用，限定作用域，避免影响其他实例
      if (!containerRef?.current) return
      const containers = containerRef.current.querySelectorAll('.echarts-container')
      
      containers.forEach((container) => {
        const el = container as HTMLElement
        const encodedOption = el.getAttribute('data-echarts-option')
        if (!encodedOption) return
        
        // 检查是否已被其他实例初始化，避免重复初始化
        const initializedBy = el.getAttribute('data-initialized-by')
        if (initializedBy && initializedBy !== instanceIdRef.current) {
          // 已被其他实例初始化，跳过
          return
        }

        try {
          // 确保容器有尺寸，ECharts 初始化必须有尺寸
          if (!el.style.height || el.style.height === '0px') {
            el.style.height = '400px'
          }
          if (!el.style.width || el.style.width === '0px') {
            el.style.width = '100%'
          }

          let chart = echarts.getInstanceByDom(el)
          
          // 如果主题发生了变化，必须销毁重建以应用新主题
          if (chart && lastThemeRef.current !== isDark) {
            chart.dispose()
            chart = undefined
          }

          if (!chart) {
            chart = echarts.init(el, isDark ? 'dark' : undefined, {
              renderer: 'svg'
            })
            // 标记此容器由当前实例初始化
            el.setAttribute('data-initialized-by', instanceIdRef.current)
          }

          const optionStr = decodeURIComponent(window.atob(encodedOption))
          const option = JSON.parse(optionStr)
          
          // 使用 notMerge: true 确保完全刷新
          chart.setOption(option, true)
          
          // 强制触发一次调整大小
          chart.resize()
        } catch (err) {
          console.error('ECharts init error:', err)
          el.innerHTML = `<div style="color: #ef4444; padding: 1rem; border: 1px solid #fee2e2; border-radius: 0.5rem; background: #fef2f2;">
            <strong>ECharts 渲染失败:</strong><br/>
            ${err instanceof Error ? err.message : '请检查配置格式是否为标准 JSON'}
          </div>`
        }
      })
      
      lastThemeRef.current = isDark
    }, 0)
  }, [isDark, containerRef])

  useEffect(() => {
    if (content) {
      initCharts()
    }
    
    // 监听 window resize 事件
    const handleResize = () => {
      resizeAllCharts()
    }
    window.addEventListener('resize', handleResize)

    // 使用 ResizeObserver 监听容器尺寸变化（比如 isFullWidth 切换时）
    if (containerRef?.current && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        // 使用 requestAnimationFrame 避免频繁触发
        requestAnimationFrame(() => {
          resizeAllCharts()
        })
      })
      resizeObserverRef.current.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      // 清理 ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [content, initCharts, containerRef, resizeAllCharts])

  return { initCharts }
}
